/**
 * WHERE "SUPPORT" GOES, IN ONE PLACE.
 *
 * `https://uselura.com/support` returned 404 on 2026-09-22 (checked by the
 * Web Director and by Web), and Book linked to it from the footer, the booking
 * outcome, the appointment receipt and the guest checkout. Product's approved
 * fallback is the support address until Marketing verifies a working page.
 *
 * A bare `mailto:` by default: no subject or body is prefilled, so no token,
 * confirmation code, name or address can ride along into a mail client (or a
 * shared mailbox) by accident. When the page exists, change it here and
 * nowhere else.
 *
 * The one exception is `supportHrefWithContext` (rc.1): a dead-end screen may
 * hand support PUBLIC, non-personal context it already has (what happened,
 * the business, the page path, the time, the build, an error reference). Its
 * input type has no field for a name, email, phone, code or token, and the
 * page path is refused outright on the bearer /a/ surface.
 */
export const SUPPORT_EMAIL = "support@uselura.com";
export const SUPPORT_HREF = `mailto:${SUPPORT_EMAIL}`;

export interface SupportContextLines {
  subject: string;
  /** Already-translated lines, in order. `null` omits a line; "" is a blank line. */
  lines: readonly (string | null)[];
}

/** The bearer appointment namespace, matched rather than spelled (no path literal). */
const BEARER_PATH = /^\/a(?:\/|$)/;

/** The page path, safe to send: no query or fragment, and never a bearer /a/ link. */
export function publicPagePath(pathname: string): string | null {
  const path = pathname.split(/[?#]/)[0] ?? "";
  if (BEARER_PATH.test(path) || !path.startsWith("/")) return null;
  return path.length > 200 ? path.slice(0, 200) : path;
}

export function supportHrefWithContext({ subject, lines }: SupportContextLines): string {
  // CRLF, as RFC 6068 asks for line breaks in a mailto body.
  const body = lines.filter((line): line is string => line !== null).join("\r\n");
  return `${SUPPORT_HREF}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}
